import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import {
  X,
  User,
  Envelope,
  Phone,
  Calendar,
  Tag,
  Link
} from '@phosphor-icons/react';
import { useBusiness } from '../../../context/BusinessContext';
import api from '../../../utils/api';
import { mergeLeadSources } from '../../../utils/crmLeadSources';

const CreateLeadModal = ({ open, onClose, onLeadCreated, leadSources = [], availableTags = [] }) => {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    weddingDate: '',
    leadSource: 'manual',
    leadSourceOther: '',
    budgetRange: {
      min: '',
      max: ''
    },
    priority: 'warm',
    tags: [],
    weddingCode: ''
  });
  const [newTag, setNewTag] = useState('');

  const sourceOptions = mergeLeadSources(leadSources);

  const priorities = [
    { value: 'high', label: 'High', color: 'error' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'low', label: 'Low', color: 'info' }
  ];

  const handleInputChange = (field, value) => {
    if (field === 'leadSource') {
      setFormData(prev => ({
        ...prev,
        leadSource: value,
        leadSourceOther: value === 'other' ? prev.leadSourceOther : ''
      }));
      return;
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddExistingTag = (tag) => {
    const cleanTag = String(tag || '').trim();
    if (!cleanTag || formData.tags.includes(cleanTag)) return;
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, cleanTag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const businessId = selectedBusiness?.business?._id;
    
    if (!formData.name || !formData.email) {
      setError('Name and email are required');
      return;
    }
    if (!businessId) {
      setError('Please select a business before creating a lead.');
      return;
    }
    if (formData.leadSource === 'other' && !formData.leadSourceOther.trim()) {
      setError('Please tell us where this enquiry came from');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Map new priority values to old values for backend compatibility
      const priorityMapToOld = {
        'high': 'hot',
        'medium': 'warm',
        'low': 'cold'
      };
      const backendPriority = formData.priority && priorityMapToOld[formData.priority]
        ? priorityMapToOld[formData.priority]
        : formData.priority;

      const response = await api.post('/api/leads', {
        businessId,
        ...formData,
        leadSourceOther: formData.leadSource === 'other' ? formData.leadSourceOther.trim() : undefined,
        priority: backendPriority,
        budgetRange: {
          min: formData.budgetRange.min ? parseInt(formData.budgetRange.min) : undefined,
          max: formData.budgetRange.max ? parseInt(formData.budgetRange.max) : undefined
        }
      });

      const lead = response.data;
      onLeadCreated(lead);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        weddingDate: '',
        leadSource: 'manual',
        leadSourceOther: '',
        budgetRange: { min: '', max: '' },
        priority: 'medium',
        tags: [],
        weddingCode: ''
      });
    } catch (err) {
      setError(err.message || 'We could not add this couple. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Add a couple
          </Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <X size={20} />
          </Button>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                InputProps={{
                  startAdornment: <User size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Envelope size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                InputProps={{
                  startAdornment: <Phone size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Wedding Date"
                type="date"
                value={formData.weddingDate}
                onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <Calendar size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message/Notes"
                multiline
                rows={3}
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder="Any extra context about this couple or enquiry…"
              />
            </Grid>

            {/* Extra fields */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Source and priority
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.leadSource}
                  onChange={(e) => handleInputChange('leadSource', e.target.value)}
                  label="Source"
                >
                  {sourceOptions.map((source) => (
                    <MenuItem key={source.value} value={source.value}>
                      {source.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formData.leadSource === 'other' && (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Where did they come from?"
                  value={formData.leadSourceOther}
                  onChange={(e) => handleInputChange('leadSourceOther', e.target.value)}
                  required
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  label="Priority"
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Wedding Code"
                value={formData.weddingCode}
                onChange={(e) => handleInputChange('weddingCode', e.target.value)}
                placeholder="6-digit wedding code"
                InputProps={{
                  startAdornment: <Link size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />
            </Grid>

            {/* Budget Range */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Budget Range
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Budget"
                type="number"
                value={formData.budgetRange.min}
                onChange={(e) => handleInputChange('budgetRange.min', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Budget"
                type="number"
                value={formData.budgetRange.max}
                onChange={(e) => handleInputChange('budgetRange.max', e.target.value)}
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  size="small"
                  placeholder="Add tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  InputProps={{
                    startAdornment: <Tag size={16} style={{ marginRight: 8, color: '#666' }} />
                  }}
                />
                <Button onClick={handleAddTag} disabled={!newTag.trim()}>
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Stack>
              {!!availableTags.length && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 1 }}>
                    Existing tags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {availableTags
                      .filter((tag) => !formData.tags.includes(tag))
                      .map((tag) => (
                        <Chip
                          key={`suggestion-${tag}`}
                          label={tag}
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddExistingTag(tag)}
                          clickable
                        />
                      ))}
                  </Stack>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Saving…' : 'Add couple'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateLeadModal;
